import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api/client.js';
import { Dialog } from '../../ui/components/Dialog/Dialog.tsx';
import { Button } from '../../ui/primitives/Button.tsx';
import { Text } from '../../ui/primitives/Text.tsx';
import styles from './profile.module.css';

/**
 * Block — the one action on this graph that asks first.
 *
 * EVERYTHING ELSE HERE IS REVERSIBLE BY REPEATING IT. Following and
 * unfollowing are the same switch; blocking is not, because unblocking
 * restores nothing: the follows it severed stay severed, in both directions.
 * A learner who blocks somebody they follow loses that follow permanently, and
 * that is worth one sentence before it happens rather than an apology
 * afterwards.
 *
 * The dialog says exactly what will be destroyed, in those words. "Tem
 * certeza?" would be a prompt about confidence; this is a prompt about
 * consequences.
 *
 * AFTER BLOCKING, THE LEARNER IS MOVED — the profile they are standing on is
 * one they have just refused, and leaving them on it would be strange. The
 * move is announced by the dialog they just accepted, so it is never an
 * unexplained reposition.
 */
export function BlockButton({ userId, name }: { userId: string; name: string }) {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  async function block() {
    setBusy(true);
    try {
      await api.block(userId);
      navigate('/perfil', { replace: true });
    } catch {
      setBusy(false);
      setAsking(false);
    }
  }

  return (
    <div className={styles.blockControl}>
      <Button variant="ghost" size="compact" onClick={() => setAsking(true)}>
        Bloquear
      </Button>

      <Dialog
        open={asking}
        title={`Bloquear ${name}?`}
        onClose={() => setAsking(false)}
        actions={
          <>
            <Button variant="ghost" onClick={() => setAsking(false)}>
              Cancelar
            </Button>
            {busy ? (
              <Button variant="destructive" disabled disabledReason="Bloqueando">
                Bloqueando…
              </Button>
            ) : (
              <Button variant="destructive" onClick={() => void block()}>
                Bloquear
              </Button>
            )}
          </>
        }
      >
        <Text as="p" variant="body">
          Vocês deixam de se seguir, nos dois sentidos, e {name} não poderá seguir você de novo.
          Desbloquear depois não devolve os follows — quem quiser seguir, segue outra vez.
        </Text>
      </Dialog>
    </div>
  );
}
